<template>
  <div class="a-cover flex">
    <Transition name="fade">
      <Header v-if="!mediaOpen" class="z-20" />
    </Transition>

    <Transition name="fade">
      <Menu
        v-if="!mediaOpen && !isGuided"
        :menuitems="categories"
        :category="category"
        arialabel="Areas of interest"
        class="z-10"
      />
    </Transition>

    <Transition name="fade">
      <TourStops
        v-if="isGuided && !mediaOpen"
        :guidedtour="guidedtour"
        :is-guided-intro="isGuidedIntro"
        class="z-10"
      />
    </Transition>

    <Transition name="fade">
      <KeepAlive>
        <!-- Renders the 'Categories' when on the campus -->
        <Categories
          v-if="isCampus && category && !isExploreAll && !mediaOpen"
          :category="category"
          class="y-inset left-0 w-85vw md:w-45vw lg:w-40vw xl:w-44vw lg:max-w-2xl z-1"
        />

        <!-- Renders when in panorama groups -->
        <Sidebar
          v-else-if="isPanorama && !mediaOpen && !isGuidedIntro"
          :post="panorama"
          :location="location"
          :is-guided="isGuided"
          :category="category"
          class="y-inset left-0 w-85vw md:w-96 z-1"
          @hotSpotClick="handleNavigation"
        />
      </KeepAlive>
    </Transition>

    <Map
      :aria-hidden="mediaOpen"
      :category="category"
      :categories="categories"
      :is-explore-all="isExploreAll"
      :is-campus-intro="isCampusIntro"
      :media-open="mediaOpen"
      class="z-0 transition"
      :class="[isCampus ? 'visible opacity-100' : 'invisible opacity-0']"
    />

    <Panorama
      v-show="isPanorama"
      key="panorama"
      :aria-hidden="mediaOpen"
      :post="panorama"
      :location="location"
      :category="category"
      class="z-0"
      @hotSpotClick="handleNavigation"
    />

    <Transition name="fade">
      <InstructionOverlay
        v-if="isGuidedIntro || isCampusIntro"
        :is-guided-intro="isGuidedIntro"
        :is-campus-intro="isCampusIntro"
        class="z-1"
      />
    </Transition>

    <Transition name="fade">
      <FocusLock v-if="mediaOpen">
        <!-- Handles the Articles Videos etc as an overlay -->
        <RouterView name="media" :location="location" class="z-1" />
      </FocusLock>
    </Transition>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { loadKrp } from '@/mixins'
import Header from '@/components/Header.vue'
import FocusLock from 'vue-focus-lock'
const Sidebar = () => import('@/components/Sidebar.vue')
const Categories = () => import('@/components/Categories.vue')
const TourStops = () => import('@/components/TourStops.vue')
const Panorama = () => import('@/components/Panorama.vue')
const Map = () => import('@/components/Map.vue')
const Menu = () => import('@/components/Menu.vue')
const InstructionOverlay = () => import('@/components/InstructionOverlay.vue')

export default {
  name: 'Viewer',

  components: {
    FocusLock,
    Panorama,
    Header,
    Sidebar,
    Categories,
    Menu,
    Map,
    TourStops,
    InstructionOverlay
  },

  mixins: [loadKrp],

  computed: {
    ...mapState(['touch', 'mobile', 'mediaOpen', 'sidebarOpen']),
    ...mapGetters(['categories', 'getPostBySlug', 'guidedtour']),

    category() {
      return this.getPostBySlug(this.$route.params.category_id)
    },

    location() {
      // Locations are 360 groups - the group is defined by media_id when in a 360
      const location = this.getPostBySlug(this.$route.params.media_id)
      // Filter 360s in the group by parents
      if (location?.type === 'location' && !this.isExploreAll) {
        // If no parents include anyway as is considered unfiltered
        // If there is a parent or parents does the array include the current category id?
        const filtered = location.children.filter(
          item => item.parents.includes(this.category.id) || !item.parents
        )

        const result = Object.assign({}, location, {
          children: filtered
        })

        return result
      } else if (!this.isGuided) {
        return location
      } else {
        return this.guidedtour
      }
    },

    panorama() {
      if (this.isGuidedIntro) {
        return this.guidedtour.children[0]
      }
      return this.getPostBySlug(this.$route.params.panorama_id)
    },

    isCampus() {
      return this.$route.meta.campus
    },

    isGuided() {
      return this.$route.meta.guided
    },

    isExploreAll() {
      return this?.category?.slug === 'explore-all'
    },

    isPanorama() {
      return this.$route.meta.panorama
    },

    isCampusIntro() {
      return this.$route.name === 'campus'
    },

    isGuidedIntro() {
      return this.$route.name === 'guided-tour'
    }
  },

  methods: {
    handleNavigation(slug) {
      if (this.isGuided) {
        this.$router
          .push({
            name: 'guided-hotspot',
            params: {
              panorama_id: this.panorama.slug,
              hotspot_id: slug
            }
          })
          .catch(() => {})
      } else if (this.isPanorama) {
        this.$router
          .push({
            name: 'campus-hotspot',
            params: {
              category_id: this.category.slug,
              panorama_id: this.panorama.slug,
              hotspot_id: slug
            }
          })
          .catch(() => {})
      }
    }
  }
}
</script>
